/**
 * File Storage Helper
 * Uses the FlexFlow Files API for file storage operations
 *
 * Supports:
 * - Upload files with progress tracking
 * - Download files
 * - Delete files (soft and hard delete)
 * - List files with filtering
 * - Entity associations (product images, order attachments, etc.)
 */

import config from "../config/app.config"

// Files endpoint base URL (app-specific)
const FILES_ENDPOINT = config.APP_URL + "files"
const APP_API_KEY = config.APP_API_KEY

// Get headers with current session token (using Bearer auth) and API key
const getHeaders = (contentType = "application/json") => {
  const authUser = JSON.parse(localStorage.getItem("authUser"))
  const token = authUser?.session_token || ""
  const headers = {
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
    "X-Api-Key": APP_API_KEY,
  }
  if (contentType) {
    headers["Content-Type"] = contentType
  }
  return headers
}

/**
 * Upload a file to FlexFlow file storage
 *
 * @param {File} file - The file to upload
 * @param {string} folder - Folder path to store the file (e.g., 'product-images', 'order-attachments')
 * @param {function} onProgress - Optional progress callback (receives percentage 0-100)
 * @param {Object} options - Additional options
 * @param {string} options.entity_type - Entity type (e.g., 'product', 'order', 'customer')
 * @param {number} options.entity_id - Entity ID to associate the file with
 * @param {boolean} options.is_public - Whether the file is publicly accessible
 * @returns {Promise<{url: string, id: number, filename: string, original_filename: string, ...}>}
 */
export const uploadFile = async (file, folder = "general", onProgress = null, options = {}) => {
  const {
    entity_type = null,
    entity_id = null,
    is_public = false
  } = options

  const formData = new FormData()
  formData.append('file', file)
  formData.append('folder', '/' + folder.replace(/^\/+/, ''))
  if (entity_type) formData.append('entity_type', entity_type)
  if (entity_id) formData.append('entity_id', String(entity_id))
  formData.append('is_public', String(is_public))

  // Get auth token
  const authUser = JSON.parse(localStorage.getItem("authUser"))
  const token = authUser?.session_token || ""

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.open("POST", `${FILES_ENDPOINT}/upload`, true)
    xhr.setRequestHeader("Authorization", token ? `Bearer ${token}` : "")
    xhr.setRequestHeader("X-Api-Key", APP_API_KEY)

    // Progress tracking
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100)
          onProgress(percent)
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText)
          const fileData = response.resource || response

          // Build the download URL for convenience
          const downloadUrl = `${FILES_ENDPOINT}/${fileData.id}/download`

          resolve({
            id: fileData.id,
            url: downloadUrl,
            filename: fileData.filename,
            original_filename: fileData.original_filename,
            name: fileData.original_filename, // Backward compatibility
            mime_type: fileData.mime_type,
            file_size: fileData.file_size,
            size: fileData.file_size, // Backward compatibility
            type: fileData.mime_type, // Backward compatibility
            folder: fileData.folder,
            entity_type: fileData.entity_type,
            entity_id: fileData.entity_id,
            is_public: fileData.is_public,
            created_at: fileData.created_at,
            uploadedAt: fileData.created_at, // Backward compatibility
          })
        } catch (err) {
          reject(new Error("Failed to parse upload response"))
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText)
          reject(new Error(error.error?.message || `Upload failed: ${xhr.status}`))
        } catch {
          reject(new Error(`Upload failed: ${xhr.statusText}`))
        }
      }
    }

    xhr.onerror = () => {
      reject(new Error("Upload failed: Network error"))
    }

    xhr.send(formData)
  })
}

/**
 * Upload multiple files
 *
 * @param {File[]} files - Array of files to upload
 * @param {string} folder - Folder path to store files
 * @param {function} onProgress - Progress callback (receives overall progress 0-100)
 * @param {Object} options - Additional options (same as uploadFile)
 * @returns {Promise<Array>}
 */
export const uploadFiles = async (files, folder = "general", onProgress = null, options = {}) => {
  const results = []
  const totalFiles = files.length

  for (let i = 0; i < files.length; i++) {
    const file = files[i]

    try {
      const fileResult = await uploadFile(file, folder, (fileProgress) => {
        if (onProgress) {
          const overallProgress = Math.round(((i + fileProgress / 100) / totalFiles) * 100)
          onProgress(overallProgress)
        }
      }, options)

      results.push({ success: true, file: fileResult })
    } catch (err) {
      results.push({ success: false, error: err.message, filename: file.name })
    }
  }

  return results
}

/**
 * Get file download URL
 *
 * @param {number} fileId - The file ID
 * @returns {string} Download URL
 */
export const getFileDownloadUrl = (fileId) => {
  return `${FILES_ENDPOINT}/${fileId}/download`
}

/**
 * Get file URL (signed URL for S3, download endpoint for local)
 *
 * @param {number} fileId - The file ID
 * @param {number} expiresIn - URL expiration time in seconds (default: 3600)
 * @returns {Promise<{url: string, expires_in: number|null, storage_type: string}>}
 */
export const getFileUrl = async (fileId, expiresIn = 3600) => {
  try {
    const response = await fetch(`${FILES_ENDPOINT}/${fileId}/url?expires_in=${expiresIn}`, {
      method: "GET",
      headers: getHeaders(),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to get file URL")
    }

    const result = await response.json()
    return result.resource || result
  } catch (error) {
    console.error("getFileUrl error:", error)
    throw error
  }
}

/**
 * Get public file URL (for backward compatibility)
 * Now returns the download endpoint URL
 *
 * @param {string} folder - The folder (ignored in new API, kept for backward compatibility)
 * @param {string} filename - The filename or file ID
 * @returns {string} File URL
 */
export const getPublicFileUrl = (folder, filename) => {
  // If filename is a number or looks like an ID, use it directly
  if (!isNaN(filename) || /^\d+$/.test(filename)) {
    return `${FILES_ENDPOINT}/${filename}/download`
  }
  // Otherwise, this is a legacy URL format - return as-is or construct download URL
  // The caller should migrate to using file IDs
  console.warn("getPublicFileUrl: Legacy URL format detected. Migrate to using file IDs.")
  return `${FILES_ENDPOINT}/${filename}/download`
}

/**
 * Download a file
 *
 * @param {number} fileId - The file ID
 * @param {string} filename - Optional filename for the download
 * @returns {Promise<Blob>}
 */
export const downloadFile = async (fileId, filename = null) => {
  try {
    const response = await fetch(`${FILES_ENDPOINT}/${fileId}/download`, {
      method: "GET",
      headers: getHeaders(null), // No Content-Type for download
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || "Failed to download file")
    }

    const blob = await response.blob()

    // If filename is provided, trigger a browser download
    if (filename) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }

    return blob
  } catch (error) {
    console.error("downloadFile error:", error)
    throw error
  }
}

/**
 * Delete a file by ID
 *
 * @param {number} fileId - The file ID
 * @param {boolean} hard - If true, permanently delete (default: soft delete)
 * @returns {Promise<boolean>}
 */
export const deleteFileById = async (fileId, hard = false) => {
  try {
    const url = hard ? `${FILES_ENDPOINT}/${fileId}?hard=true` : `${FILES_ENDPOINT}/${fileId}`
    const response = await fetch(url, {
      method: "DELETE",
      headers: getHeaders(),
    })

    return response.ok
  } catch (error) {
    console.error("deleteFileById error:", error)
    return false
  }
}

/**
 * Delete a file (legacy function - wraps deleteFileById)
 *
 * @param {string} folder - The folder (ignored in new API)
 * @param {string|number} filename - The filename or file ID
 * @returns {Promise<boolean>}
 */
export const deleteFile = async (folder, filename) => {
  // If filename is a number or looks like an ID, use deleteFileById
  if (!isNaN(filename) || /^\d+$/.test(filename)) {
    return deleteFileById(parseInt(filename, 10))
  }

  console.warn("deleteFile: Legacy format detected. Migrate to using file IDs.")
  return false
}

/**
 * Delete a file by its info object
 *
 * @param {object} fileInfo - File info object (from uploadFile result)
 * @returns {Promise<boolean>}
 */
export const deleteFileByInfo = async (fileInfo) => {
  if (fileInfo.id) {
    return deleteFileById(fileInfo.id)
  }
  console.warn("deleteFileByInfo: No file ID in fileInfo. Cannot delete.")
  return false
}

/**
 * List files with filtering
 *
 * @param {string|Object} folderOrOptions - Folder path string or options object
 * @param {string} folderOrOptions.folder - Filter by folder path
 * @param {string} folderOrOptions.entity_type - Filter by entity type
 * @param {number} folderOrOptions.entity_id - Filter by entity ID
 * @param {number} folderOrOptions.limit - Number of records (default: 50)
 * @param {number} folderOrOptions.offset - Starting position (default: 0)
 * @param {boolean} folderOrOptions.include_deleted - Include soft-deleted files
 * @returns {Promise<Array>}
 */
export const listFiles = async (folderOrOptions = {}) => {
  // Handle backward compatibility: if string is passed, treat as folder
  const options = typeof folderOrOptions === "string"
    ? { folder: folderOrOptions }
    : folderOrOptions

  const {
    folder,
    entity_type,
    entity_id,
    limit = 50,
    offset = 0,
    include_deleted = false
  } = options

  const params = new URLSearchParams()
  params.append("limit", String(limit))
  params.append("offset", String(offset))
  if (folder) params.append("folder", "/" + folder.replace(/^\/+/, ""))
  if (entity_type) params.append("entity_type", entity_type)
  if (entity_id) params.append("entity_id", String(entity_id))
  if (include_deleted) params.append("include_deleted", "true")

  try {
    const response = await fetch(`${FILES_ENDPOINT}?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(),
    })

    if (!response.ok) {
      return []
    }

    const result = await response.json()
    return result.resource || []
  } catch (error) {
    console.error("listFiles error:", error)
    return []
  }
}

/**
 * Get files for a specific entity
 *
 * @param {string} entityType - Entity type (e.g., 'product', 'order', 'customer')
 * @param {number} entityId - Entity ID
 * @returns {Promise<Array>}
 */
export const getEntityFiles = async (entityType, entityId) => {
  return listFiles({
    entity_type: entityType,
    entity_id: entityId,
    limit: 100
  })
}

/**
 * Create a folder (no-op in new API - folders are auto-created on upload)
 *
 * @param {string} folderPath - The folder path to create
 * @returns {Promise<boolean>}
 */
export const createFolder = async (folderPath) => {
  // In the new API, folders are created automatically on upload
  // This function is kept for backward compatibility
  console.info("createFolder: Folders are auto-created on upload in the new API")
  return true
}

/**
 * Get file metadata
 *
 * @param {number} fileId - The file ID
 * @returns {Promise<Object|null>}
 */
export const getFileMetadata = async (fileId) => {
  try {
    const response = await fetch(`${FILES_ENDPOINT}/${fileId}`, {
      method: "GET",
      headers: getHeaders(),
    })

    if (!response.ok) {
      return null
    }

    const result = await response.json()
    return result.resource || result
  } catch (error) {
    console.error("getFileMetadata error:", error)
    return null
  }
}

/**
 * Update file metadata
 *
 * @param {number} fileId - The file ID
 * @param {Object} data - Update data (folder, entity_type, entity_id, is_public)
 * @returns {Promise<Object|null>}
 */
export const updateFileMetadata = async (fileId, data) => {
  try {
    const response = await fetch(`${FILES_ENDPOINT}/${fileId}`, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      return null
    }

    const result = await response.json()
    return result.resource || result
  } catch (error) {
    console.error("updateFileMetadata error:", error)
    return null
  }
}

export default {
  uploadFile,
  uploadFiles,
  downloadFile,
  deleteFile,
  deleteFileById,
  deleteFileByInfo,
  listFiles,
  getEntityFiles,
  createFolder,
  getPublicFileUrl,
  getFileUrl,
  getFileDownloadUrl,
  getFileMetadata,
  updateFileMetadata,
}
